import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ClientMessage,
  ServerMessage,
  ConnectionStatus,
} from "../types";

/** Configuration for the WebSocket connection */
interface UseWebSocketOptions {
  /** WebSocket URL (defaults to ws://localhost:9000/ws) */
  url?: string;
  /** Auto-reconnect on disconnect (default: true) */
  autoReconnect?: boolean;
  /** Max reconnect attempts (default: 10) */
  maxReconnectAttempts?: number;
  /** Base reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Called when a message is received */
  onMessage?: (message: ServerMessage) => void;
  /** Called when connection status changes */
  onStatusChange?: (status: ConnectionStatus) => void;
}

interface UseWebSocketReturn {
  /** Current connection status */
  status: ConnectionStatus;
  /** Send a message to the server */
  send: (message: ClientMessage) => void;
  /** Manually connect */
  connect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** Last error message, if any */
  lastError: string | null;
}

/**
 * WebSocket hook for the HVME gateway connection.
 *
 * Handles:
 * - Connection lifecycle
 * - Auto-reconnect with exponential backoff
 * - Message serialization/deserialization
 * - Connection status tracking
 */
export function useWebSocket(
  options: UseWebSocketOptions = {},
): UseWebSocketReturn {
  const {
    url = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws`,
    autoReconnect = true,
    maxReconnectAttempts = 10,
    reconnectDelay = 1000,
    onMessage,
    onStatusChange,
  } = options;

  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [lastError, setLastError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  const mountedRef = useRef(true);

  // Keep callback refs current without triggering reconnection
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    if (!mountedRef.current) return;
    setStatus(newStatus);
    onStatusChangeRef.current?.(newStatus);
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
    }

    updateStatus("connecting");
    setLastError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        reconnectAttemptRef.current = 0;
        updateStatus("connected");
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const message = JSON.parse(event.data as string) as ServerMessage;
          onMessageRef.current?.(message);
        } catch {
          console.error("[HVME WS] Failed to parse message:", event.data);
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        setLastError("WebSocket connection error");
        updateStatus("error");
      };

      ws.onclose = (event: CloseEvent) => {
        if (!mountedRef.current) return;
        wsRef.current = null;

        // Don't reconnect on normal closure (code 1000) or if disabled
        if (!autoReconnect || event.code === 1000) {
          updateStatus("disconnected");
          return;
        }

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptRef.current < maxReconnectAttempts) {
          updateStatus("reconnecting");
          const delay =
            reconnectDelay *
            Math.pow(2, Math.min(reconnectAttemptRef.current, 6));
          const jitter = delay * 0.2 * Math.random();

          reconnectTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            reconnectAttemptRef.current += 1;
            connect();
          }, delay + jitter);
        } else {
          setLastError(
            `Failed to reconnect after ${maxReconnectAttempts} attempts`,
          );
          updateStatus("error");
        }
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Connection failed";
      setLastError(message);
      updateStatus("error");
    }
  }, [
    url,
    autoReconnect,
    maxReconnectAttempts,
    reconnectDelay,
    updateStatus,
  ]);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    reconnectAttemptRef.current = maxReconnectAttempts; // Prevent auto-reconnect

    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnect");
      wsRef.current = null;
    }

    updateStatus("disconnected");
  }, [clearReconnectTimer, maxReconnectAttempts, updateStatus]);

  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn("[HVME WS] Cannot send, socket not open. Status:", status);
    }
  }, [status]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, "Component unmount");
        wsRef.current = null;
      }
    };
  }, [clearReconnectTimer]);

  return { status, send, connect, disconnect, lastError };
}
